import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import { IListBorderoData } from 'src/Contexts/Lancamentos/Contrato/ContratosTypes';
import { FormatDateBR, GetDateUSSemValue } from 'src/shared/Utils/FormatDateBR';
import { FormatMoney } from 'src/shared/Utils/FormatMoney';
import { cpfOurCnpj } from 'src/shared/Utils/MaskCpfCnpj';
import { valorPorExtenso } from 'src/shared/Utils/ValorPorExtenso';

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 12,
    fontFamily: 'Helvetica',
    color: '#1f2937',
  },
  container: {
    flex: 1,
    flexDirection: 'column',
  },
  header: {
    marginBottom: 14,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#3980F5',
    marginBottom: 4,
  },
  empresaNome: {
    fontSize: 10,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 2,
  },
  dataRecebimentoHeader: {
    fontSize: 10,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 12,
  },
  section: {
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 7,
    borderBottom: '1px solid #e5e7eb',
    paddingBottom: 4,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 6,
    justifyContent: 'space-between',
  },
  label: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 'bold',
    width: '40%',
  },
  value: {
    fontSize: 11,
    color: '#1f2937',
    width: '60%',
    textAlign: 'right',
  },
  valueHighlight: {
    fontSize: 13,
    color: '#3cb916',
    fontWeight: 'bold',
    width: '60%',
    textAlign: 'right',
  },
  divider: {
    borderTop: '1px solid #e5e7eb',
    marginVertical: 10,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTop: '1px solid #e5e7eb',
  },
  footerText: {
    fontSize: 10,
    color: '#6b7280',
    textAlign: 'center',
    marginTop: 8,
  },
  signatureLine: {
    marginTop: 32,
    borderTop: '1px solid #1f2937',
    paddingTop: 4,
    textAlign: 'center',
    fontSize: 10,
  },
  declaracao: {
    marginTop: 12,
    marginBottom: 4,
    fontSize: 11,
    color: '#1f2937',
    textAlign: 'center',
    lineHeight: 1.4,
  },
});

interface IReciboRecebimentoPDFProps {
  parcela: IListBorderoData;
  nomeEmpresa?: string;
}

const ReciboRecebimentoPDF = ({
  parcela,
  nomeEmpresa,
}: IReciboRecebimentoPDFProps) => {
  const clienteNome =
    parcela.tipo_contrato === 'PF'
      ? parcela.titular_pf
      : parcela.razao_social_pj;
  const clienteDocumento = cpfOurCnpj({
    text: String(parcela.cpf_cnpj),
    type: parcela.tipo_contrato === 'PF' ? 'cpf' : 'cnpj',
  });

  const numeroParcela =
    String(parcela?.numero_parcela ?? '0') === '0' ? 'Adesão' : `${String(parcela?.numero_parcela ?? '0')}ª parcela`;

  const valorExtenso = valorPorExtenso(
    parcela.valor_recebido || parcela.valor_contrato || '0'
  );
  const docLabel = parcela.tipo_contrato === 'PF' ? 'CPF' : 'CNPJ';
  const fraseDeclaracao = `Declaro ter recebido de ${String(clienteNome ?? '').toUpperCase()} - ${docLabel} ${clienteDocumento} a importância de ${valorExtenso.toUpperCase()}.`;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.container}>
          <View style={styles.header}>
            <Text style={styles.subtitle}>Comprovante de Pagamento</Text>
            {nomeEmpresa && (
              <Text style={styles.empresaNome}>{nomeEmpresa}</Text>
            )}
            {parcela.data_recebimento && (
              <Text style={styles.dataRecebimentoHeader}>
                Data de Recebimento: {FormatDateBR(parcela.data_recebimento)}
              </Text>
            )}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dados do Cliente</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Nome/Razão Social:</Text>
              <Text style={styles.value}>{clienteNome}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>
                {parcela.tipo_contrato === 'PF' ? 'CPF:' : 'CNPJ:'}
              </Text>
              <Text style={styles.value}>{clienteDocumento}</Text>
            </View>
          </View>

          <View style={styles.divider} />

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dados do Contrato</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Código da Proposta:</Text>
              <Text style={styles.value}>{parcela.cod_proposta}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Operadora:</Text>
              <Text style={styles.value}>
                {parcela.name_operadoras}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Número de Vidas:</Text>
              <Text style={styles.value}>{parcela.contratos_vidas}</Text>
            </View>
          </View>

          <View style={styles.divider} />

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Dados do Recebimento</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Parcela:</Text>
              <Text style={styles.value}>{numeroParcela}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Data de Vencimento:</Text>
              <Text style={styles.value}>
                {parcela.data_vencimento_parcela
                  ? FormatDateBR(parcela.data_vencimento_parcela)
                  : '-'}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Data de Recebimento:</Text>
              <Text style={styles.value}>
                {parcela.data_recebimento
                  ? FormatDateBR(parcela.data_recebimento)
                  : '-'}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Valor Recebido:</Text>
              <Text style={styles.valueHighlight}>
                R$ {FormatMoney(parcela.valor_recebido || parcela.valor_contrato)}
              </Text>
            </View>
          </View>

          <Text style={styles.declaracao}>{fraseDeclaracao}</Text>

          <View style={styles.footer}>
            <View style={styles.signatureLine}>
              <Text>{nomeEmpresa || 'Brokeris'}</Text>
            </View>
            <Text style={styles.footerText}>
              Documento gerado em {FormatDateBR(GetDateUSSemValue())}
            </Text>
            <Text style={styles.footerText}>
              Este documento comprova o recebimento do valor acima mencionado.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

/**
 * Função para gerar e fazer download do PDF do recibo de recebimento
 */
export const handleDownloadReciboRecebimento = async (
  parcela: IListBorderoData,
  nomeEmpresa?: string
) => {
  const blob = await pdf(
    <ReciboRecebimentoPDF parcela={parcela} nomeEmpresa={nomeEmpresa} />
  ).toBlob();
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;

  const clienteNome =
    parcela.tipo_contrato === 'PF'
      ? parcela.titular_pf
      : parcela.razao_social_pj;

  const nomeArquivo = `Comprovante de Pagamento - ${clienteNome?.trim()} - ${parcela?.cod_proposta?.trim()}-${String(parcela?.numero_parcela ?? '0').trim()}.pdf`;
  link.download = nomeArquivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
